<template>
  <div class="v2-table-wrapper">
    <table class="v2-table">
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.key"
            :style="col.width ? { width: col.width } : {}"
            @click="col.sortable ? $emit('sort', col.key) : null"
          >
            {{ col.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="loading">
          <td
            :colspan="columns.length"
            class="v2-table__loading"
          >
            <v2-spinner size="sm" />
          </td>
        </tr>
        <tr v-else-if="!items.length">
          <td
            :colspan="columns.length"
            class="v2-table__empty"
          >
            <slot name="empty">
              No data available
            </slot>
          </td>
        </tr>
        <tr
          v-for="(item, idx) in items"
          v-else
          :key="item._id || idx"
          :class="{ 'v2-table__row--clickable': clickableRows }"
          @click="clickableRows && $emit('row-click', item)"
        >
          <td
            v-for="col in columns"
            :key="col.key"
          >
            <slot
              :name="`cell(${col.key})`"
              :item="item"
              :value="item[col.key]"
            >
              {{ item[col.key] }}
            </slot>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import V2Spinner from './V2Spinner.vue'

export default {
  name: 'V2Table',
  components: { V2Spinner },
  props: {
    columns: { type: Array, required: true },
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    clickableRows: { type: Boolean, default: false },
  },
}
</script>

<style lang="scss" scoped>
.v2-table-wrapper {
  overflow-x: auto;
}

.v2-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  // Header tint glass: subtle surface-2 fill so the header reads as a
  // discrete band over the page mesh. backdrop-filter intentionally omitted
  // here — sticky thead + backdrop-filter has known repaint thrash on
  // Firefox and most tables don't need a second blur layer when the
  // surrounding card is already glass-panel.
  th {
    text-align: left;
    padding: var(--v2-space-3) var(--v2-space-4);
    font-weight: 500;
    color: var(--v2-muted-foreground);
    background: var(--v2-glass-surface-2);
    border-bottom: 1px solid var(--v2-glass-stroke);
    white-space: nowrap;
  }

  td {
    padding: var(--v2-space-3) var(--v2-space-4);
    border-bottom: 1px solid var(--v2-glass-stroke);
    color: var(--v2-foreground);
  }

  tbody tr:last-child td { border-bottom: none; }

  tbody tr:hover { background: var(--v2-glass-surface-2); }

  tbody tr.v2-table__row--clickable { cursor: pointer; }

  &__loading,
  &__empty {
    text-align: center;
    padding: var(--v2-space-8) var(--v2-space-4);
    color: var(--v2-muted-foreground);
  }
}

@media (prefers-reduced-transparency: reduce) {
  .v2-table th {
    background: var(--v2-muted);
    border-bottom-color: var(--v2-border);
  }
  .v2-table td { border-bottom-color: var(--v2-border); }
  .v2-table tbody tr:hover { background: var(--v2-muted); }
}
</style>
